import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PortalLoadingCard, PortalStateCard, derivePortalTheme } from './portalShared'

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 8,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #dce6df'
  },
  cardTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  helperText: {
    color: '#5d6c64'
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  colorSwatch: {
    width: 44,
    height: 44,
    borderRadius: 8,
    border: '2px solid #ddd',
    cursor: 'pointer',
    padding: 0,
    appearance: 'none',
    '&::-webkit-color-swatch-wrapper': { padding: 0 },
    '&::-webkit-color-swatch': { border: 'none', borderRadius: 6 }
  },
  previewStrip: {
    borderRadius: 8,
    overflow: 'hidden',
    border: '1px solid #ddd',
    marginTop: theme.spacing(2)
  },
  logoZone: {
    border: '2px dashed #ccc',
    borderRadius: 8,
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': { borderColor: '#999' }
  },
  logoPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3)
  }
}))

const SubcontractorSettings = ({ user, notification }) => {
  const classes = useStyles()
  const fileInputRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [primaryColor, setPrimaryColor] = useState('')
  const [logoUrl, setLogoUrl] = useState('')

  const loadBranding = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axios.get('/api/subcontractor/branding')
      setDisplayName(res.data?.displayName || '')
      setPrimaryColor(res.data?.primaryColor || '')
      setLogoUrl(res.data?.logoUrl || '')
    } catch (err) {
      console.error('Failed to load branding', err)
      setError(err?.response?.data?.message || 'Unable to load branding settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadBranding() }, [loadBranding])

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await axios.put('/api/subcontractor/branding', { displayName, primaryColor })
      setDisplayName(res.data?.displayName || '')
      setPrimaryColor(res.data?.primaryColor || '')
      notification?.({ type: 'success', title: 'Branding Saved', message: 'Portal branding updated. Refresh the page to see header changes.' })
    } catch (err) {
      console.error('Failed to save branding', err)
      notification?.({ type: 'danger', title: 'Save Failed', message: err?.response?.data?.message || 'Unable to save branding' })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    setSaving(true)
    try {
      const res = await axios.put('/api/subcontractor/branding', { displayName: '', primaryColor: '' })
      setDisplayName(res.data?.displayName || '')
      setPrimaryColor(res.data?.primaryColor || '')
      notification?.({ type: 'success', title: 'Branding Reset', message: 'Portal branding reset to defaults.' })
    } catch (err) {
      console.error('Failed to reset branding', err)
      notification?.({ type: 'danger', title: 'Reset Failed', message: err?.response?.data?.message || 'Unable to reset branding' })
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (file) => {
    if (!file) return
    const formData = new FormData()
    formData.append('logo', file)
    setUploading(true)
    try {
      const res = await axios.post('/api/subcontractor/branding/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setLogoUrl(res.data?.logoUrl || '')
      notification?.({ type: 'success', title: 'Logo Uploaded', message: 'Your logo will appear in the portal header.' })
    } catch (err) {
      console.error('Failed to upload logo', err)
      notification?.({ type: 'danger', title: 'Upload Failed', message: err?.response?.data?.message || 'Unable to upload logo' })
    } finally {
      setUploading(false)
    }
  }

  const handleLogoRemove = async () => {
    setUploading(true)
    try {
      await axios.delete('/api/subcontractor/branding/logo')
      setLogoUrl('')
      notification?.({ type: 'success', title: 'Logo Removed', message: 'Logo has been removed from the portal.' })
    } catch (err) {
      console.error('Failed to remove logo', err)
      notification?.({ type: 'danger', title: 'Remove Failed', message: err?.response?.data?.message || 'Unable to remove logo' })
    } finally {
      setUploading(false)
    }
  }

  const previewTheme = derivePortalTheme(primaryColor)
  const previewName = displayName || user?.subcontractorProfile?.vendorName || 'Your Company'

  if (loading) {
    return <PortalLoadingCard title="Loading settings" message="Pulling portal branding configuration." minHeight={240} />
  }

  if (error) {
    return <PortalStateCard title="Settings unavailable" message={error} actionLabel="Retry" onAction={loadBranding} tone="danger" minHeight={240} />
  }

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h6" className={classes.cardTitle}>Portal Branding</Typography>
        <Typography variant="body2" className={classes.helperText}>Customize how your portal looks for your team.</Typography>

        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                helperText="Shown in the header. Leave blank to use your vendor name."
                inputProps={{ maxLength: 100 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#5d6c64', marginBottom: 6 }}>Primary Color</Typography>
              <div className={classes.colorRow}>
                <input
                  type="color"
                  className={classes.colorSwatch}
                  value={primaryColor || '#1b4d3c'}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                <TextField
                  variant="outlined"
                  size="small"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#1b4d3c"
                  style={{ flex: 1 }}
                />
              </div>
              <Typography variant="caption" style={{ color: '#888', marginTop: 4, display: 'block' }}>
                Controls header, tabs, and accent colors throughout the portal.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#5d6c64', marginBottom: 8 }}>Preview</Typography>
              <div className={classes.previewStrip}>
                <div style={{ background: previewTheme.headerGradient, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
                  {logoUrl ? <img src={logoUrl} alt="" style={{ height: 24, objectFit: 'contain', borderRadius: 3 }} /> : null}
                  <span style={{ color: previewTheme.textOnPrimary, fontWeight: 600, fontSize: 13 }}>{previewName}</span>
                </div>
                <div style={{ background: previewTheme.tabBarBg, padding: '4px 16px', display: 'flex', gap: 0 }}>
                  <span style={{ color: previewTheme.textOnPrimary, padding: '4px 10px', fontSize: 11, borderBottom: `2px solid ${previewTheme.indicatorColor}` }}>Dashboard</span>
                  <span style={{ color: previewTheme.textOnPrimaryMuted, padding: '4px 10px', fontSize: 11 }}>Schedule</span>
                  <span style={{ color: previewTheme.textOnPrimaryMuted, padding: '4px 10px', fontSize: 11 }}>Settings</span>
                </div>
              </div>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontWeight: 600, color: '#5d6c64', marginBottom: 8 }}>Logo</Typography>
              {logoUrl ? (
                <div className={classes.logoPreview}>
                  <img src={logoUrl} alt="Current logo" style={{ height: 48, objectFit: 'contain', borderRadius: 6, border: '1px solid #ddd' }} />
                  <Button variant="outlined" color="secondary" size="small" onClick={handleLogoRemove} disabled={uploading}>
                    {uploading ? 'Removing...' : 'Remove Logo'}
                  </Button>
                </div>
              ) : (
                <div
                  className={classes.logoZone}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleLogoUpload(e.dataTransfer.files[0]) }}
                >
                  <Typography variant="body2" style={{ color: '#888' }}>
                    {uploading ? 'Uploading...' : 'Drop an image here or click to upload'}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#aaa' }}>PNG or JPG, max 2MB</Typography>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={(e) => handleLogoUpload(e.target.files[0])}
              />
            </Grid>
          </Grid>
        </Box>

        <div className={classes.actionRow}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Branding'}
          </Button>
          <Button variant="outlined" onClick={handleReset} disabled={saving}>
            Reset to Default
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorSettings)
